import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Modal, Space, Spin, Table, Typography, message } from 'antd';
import { API } from '@/services/api.service';
import { errorText, formatAmount } from '../format';

const { Text } = Typography;

/**
 * An investments file already sent to the group, set against the ledger. It
 * becomes the company's form, and its months are frozen, only when the
 * accountant says so.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company
 * @param {File} props.file - The .xlsx
 * @param {number} props.year - Year the file is for
 * @param {Function} props.onClose - Close without taking anything over
 * @param {Function} props.onApplied - Called after the file is taken over
 * @returns {JSX.Element} The dialog
 */
export default function InvestmentsImportModal({ companyId, file, year, onClose, onApplied }) {
  const { t } = useTranslation('clientReports');

  const previewMutation = useMutation({
    mutationFn: () => API.clientReports.importInvestments(companyId, file, { year, apply: false }),
    onError: async (e) => {
      message.error(await errorText(e));
      onClose();
    },
  });
  const applyMutation = useMutation({
    mutationFn: () => API.clientReports.importInvestments(companyId, file, { year, apply: true }),
    onSuccess: () => {
      message.success(t('investments.importApplied'));
      onApplied();
    },
    onError: async (e) => message.error(await errorText(e)),
  });

  useEffect(() => {
    previewMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const result = previewMutation.data?.data;
  const labelOf = new Map((result?.rows || []).map((r) => [r.key, r.label]));
  const differences = (result?.months || []).flatMap((mo) =>
    mo.differences.map((key) => ({
      key: `${mo.month}-${key}`,
      month: mo.month,
      row: labelOf.get(key) || key,
      inFile: mo.values[key] || 0,
      ledger: mo.computed[key] || 0,
    }))
  );

  return (
    <Modal
      open
      width={900}
      title={t('investments.importTitle')}
      onCancel={onClose}
      footer={
        <Space>
          <Button onClick={onClose}>{t('fccs.cancel')}</Button>
          <Button
            type="primary"
            className="btn-primary"
            disabled={!result}
            loading={applyMutation.isPending}
            onClick={() => applyMutation.mutate()}
          >
            {t('investments.importApply')}
          </Button>
        </Space>
      }
    >
      {!result && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      )}
      {result && (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Text>
            {t('investments.importSummary', {
              year: result.year,
              sheet: result.sheetName,
              rows: result.rows.length,
            })}
          </Text>
          <Text>
            {result.months.length
              ? t('investments.importMonths', {
                months: result.months.map((mo) => String(mo.month).padStart(2, '0')).join(', '),
                same: result.months.filter((mo) => !mo.differences.length).length,
              })
              : t('investments.importNoMonths')}
          </Text>
          {result.formYear !== result.year && (
            <Alert
              type="warning"
              showIcon
              message={t('investments.importOtherYear', { formYear: result.formYear, year: result.year })}
            />
          )}
          {result.unknownRows.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={t('investments.importUnknownRows', { list: result.unknownRows.join(', ') })}
            />
          )}
          {differences.length > 0 && (
            <Table
              size="small"
              pagination={false}
              scroll={{ y: 320 }}
              rowKey="key"
              dataSource={differences}
              columns={[
                {
                  title: t('investments.month'),
                  dataIndex: 'month',
                  key: 'month',
                  width: 80,
                  render: (m) => `${String(m).padStart(2, '0')}.${result.year}`,
                },
                { title: t('investments.row'), dataIndex: 'row', key: 'row' },
                {
                  title: t('fccs.inFile'),
                  dataIndex: 'inFile',
                  key: 'inFile',
                  align: 'right',
                  width: 130,
                  render: formatAmount,
                },
                {
                  title: t('investments.fromLedger'),
                  dataIndex: 'ledger',
                  key: 'ledger',
                  align: 'right',
                  width: 130,
                  render: formatAmount,
                },
              ]}
            />
          )}
          <Alert type="info" showIcon message={t('investments.importNote')} />
        </Space>
      )}
    </Modal>
  );
}
